import React, { useMemo } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { ItemType, Link as LinkStyle } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import { PortfoliosActionsMenu } from '../components/PortfoliosActionsMenu';
import { Dates, formatAllocation } from 'venn-utils';
import type { LibrarySearchEntity } from 'venn-api';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import PortfolioIcon from '../components/PortfolioIcon';
import {
  renderSharpeRatio,
  renderTaxStatus,
  renderTimespan,
  renderTotalReturn,
  renderVolatility,
} from '../../../../home-page/src/components/Shared';

export const getItemType = (item: LibrarySearchEntity) => {
  if (item.portfolioId) {
    if (item.aggregatedPortfolio) {
      return ItemType.LiveAggregatedPortfolio;
    }
    return item.investmentSubtype === 'PORTFOLIO_HISTORICAL' ? ItemType.HistoricalPortfolio : ItemType.Portfolio;
  }

  return ItemType.PrivatePortfolio;
};

export const getNameRenderer = () => {
  function NameRenderer(item: ICellRendererParams<LibrarySearchEntity>) {
    if (!item.data) {
      return undefined;
    }

    return (
      <div className={cn('flex items-center h-full w-full')}>
        <Tooltip disableHoverableContent>
          <span className={cn('flex items-center gap-[10px] max-w-full')}>
            <PortfolioIcon
              itemType={getItemType(item.data)}
              dataSource={item.data.dataSource}
              accountStatus={item.data.accountStatus}
              lastSyncStatus={item.data.lastSyncStatus}
            />
            <TooltipTrigger asChild>
              <LinkStyle className={cn('flex-1 overflow-ellipsis overflow-hidden text-left')} refreshedStyling>
                {item.data.name ?? '—'}
              </LinkStyle>
            </TooltipTrigger>
          </span>
          <TooltipPortal>
            <TooltipContentWithArrow side="top" className="p-[8px] max-w-[400px]">
              <div className="flex flex-col gap-[12px]">
                <div className="tooltip-title text-[14px] font-bold">{item.data.name ?? '—'}</div>
                <div className="tooltip-content text-[12px] [&_.tooltip-value]:font-bold [&_p]:m-0">
                  {item.data.portfolioId &&
                    renderTimespan('Availability', item.data.startRange, item.data.endRange, item.data.frequency)}
                  {item.data.fundId &&
                    renderTimespan(
                      'Availability',
                      item.data.unproxiedStartRange,
                      item.data.unproxiedEndRange,
                      item.data.unproxiedFrequency,
                    )}
                  {item.data.taxStatuses && item.data.taxStatuses.length > 0 && renderTaxStatus(item.data)}
                  {renderTotalReturn(item.data)}
                  {renderVolatility(item.data)}
                  {renderSharpeRatio(item.data)}
                </div>
              </div>
            </TooltipContentWithArrow>
          </TooltipPortal>
        </Tooltip>
      </div>
    );
  }

  return NameRenderer;
};

export const totalAssetsRenderer = (item: ICellRendererParams<LibrarySearchEntity>) => {
  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <span>
          {item?.data?.totalAssetValue
            ? formatAllocation(item?.data?.totalAssetValue, true, false, undefined, false, 1)
            : '-'}
        </span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContentWithArrow side="top">
          <div className={cn('grid grid-cols-2 text-[12px]')}>
            <span>Last updated:</span>
            <b className={cn('text-right')}>
              {item.data?.lastPortfolioMetadataSyncTime
                ? Dates.toDDMMMYYYY(item.data.lastPortfolioMetadataSyncTime)
                : '—'}
            </b>
            <span>Last available:</span>
            <b className={cn('text-right')}>
              {item.data?.allocationsAsOf ? Dates.toDDMMMYYYY(item.data?.allocationsAsOf) : '—'}
            </b>
          </div>
        </TooltipContentWithArrow>
      </TooltipPortal>
    </Tooltip>
  );
};

export const menuRenderer = (item: ICellRendererParams<LibrarySearchEntity>) => {
  if (!item.data) {
    return undefined;
  }

  return <PortfoliosActionsMenu item={item.data} />;
};

export const usePortfoliosLibraryColDefs = (): ColDef<LibrarySearchEntity>[] => {
  return useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Portfolio',
        cellRenderer: getNameRenderer(),
        width: 325,
      },
      {
        field: 'householdName',
        headerName: 'Household',
        minWidth: 175,
        flex: 1,
      },
      {
        field: 'ownerContextFriendlyName',
        headerName: 'Ownership',
        minWidth: 175,
        flex: 1,
      },
      {
        field: 'clientName',
        headerName: 'Client',
        flex: 1,
        minWidth: 175,
      },
      {
        field: 'custodian',
        headerName: 'Custodian',
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'program',
        headerName: 'Program',
        flex: 1,
        minWidth: 175,
      },
      {
        field: 'managedModel',
        headerName: 'Managed Model',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'registrationType',
        headerName: 'Registration Type',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'accountStatus',
        headerName: 'Status',
        width: 75,
      },
      {
        field: 'totalAssetValue',
        headerName: 'Total Asset Value (USD)',
        headerClass: 'ag-right-aligned-header',
        cellRenderer: totalAssetsRenderer,
        minWidth: 175,
        cellStyle: {
          textAlign: 'right',
        },
      },
      {
        headerName: 'Actions',
        cellRenderer: menuRenderer,
        sortable: false,
        width: 75,
        pinned: 'right' as const,
        cellStyle: {
          textAlign: 'center',
        },
      },
    ],
    [],
  );
};
